import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import Icon from '../../../../components/ui/Icon/Icon'

import * as ExploreApiService from '../../../../services/api/requests/Explore'

import { setGACustomEvent } from '../../../../services/analytics/google-analytics'

const MainPage = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  height: 100%;

  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }
`

const ModalContent = styled.div`
  @media (max-width: 1024px) {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
`

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  min-height: 87px;
  font-size: 0.7rem;
  z-index: 100;

  ${props =>
    props.hasScrolled &&
    css`
      background: linear-gradient(var(--color-primary), var(--color-primary));
    `}

  @media (max-width: 1024px) {
    position: relative;
    background: var(--color-primary);
    border-radius: 16px 16px 0 0;
    min-height: 60px;
  }
`

const ReturnIcon = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  cursor: pointer;
  font-size: 0.92rem;
  margin-left: 20px !important;
  margin-right: 20px !important;
  color: var(--color-fg-light);
  background: rgba(255, 255, 255, 0.5);
  z-index: 1;

  & > svg {
    position: relative;
    color: #ffffff;
  }
`

const Title = styled.h1`
  font-family: var(--font-family-ayala-display-sans);
  flex: 1;
  padding: 20px 0;
  margin: 0;
  margin-right: 60px;
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: var(--font-weight-boldest);
  text-transform: uppercase;
  text-align: center;
`

const PageBody = styled.div`
  &::before {
    content: ' ';
    display: block;
    height: 5rem;
    background-color: var(--color-primary);
    background: linear-gradient(var(--color-primary), var(--color-primary));
  }

  @media (max-width: 1024px) {
    &::before {
      display: none;
    }
    flex: 1;
    overflow-y: auto;
  }
`

const SurveyContainer = styled.div`
  padding: 1.5rem 1.1875rem;
  background: white;
`

const SurveyTitle = styled.h2`
  font-family: var(--font-family-ayala-display-sans);
  color: #000;
  font-size: 1.5rem;
  font-weight: var(--font-weight-boldest);
  margin-bottom: 0.5rem;
  text-align: left;
`

const SurveyDescription = styled.p`
  color: #000;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 2rem;
  text-align: left;
`

const SurveyForm = styled.form`
  display: flex;
  flex-direction: column;
`

const QuestionCard = styled.div`
  background: #f5f5f5;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
`

const QuestionLabel = styled.label`
  color: #000;
  font-size: 0.875rem;
  font-weight: var(--font-weight-bold);
  display: block;
  margin-bottom: 1rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
`

const YesNoButton = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  color: #000;
  font-size: 0.875rem;
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
  }

  ${props =>
    props.selected &&
    `
    background: var(--color-primary);
    color: white;
    border-color: var(--color-primary);
  `}
`

const RatingButton = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  color: #000;
  font-size: 0.875rem;
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
  }

  ${props =>
    props.selected &&
    `
    background: var(--color-primary);
    color: white;
    border-color: var(--color-primary);
  `}
`

const SubmitButton = styled.button`
  background: #ee7726;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  margin-top: 1rem;
  width: 100%;
  transition: all 0.2s ease;

  &:disabled {
    color: #666;
    background: #e0e0e0;
    cursor: not-allowed;
  }
`

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

// Success Modal Styles
const SuccessModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
`

const SuccessModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
`

const SuccessModalTitle = styled.h2`
  font-family: var(--font-family-ayala-display-sans);
  color: #000;
  font-size: 1.5rem;
  font-weight: var(--font-weight-boldest);
  margin: 0 0 1rem 0;
`

const SuccessModalMessage = styled.p`
  color: #000;
  font-size: 0.875rem;
  margin: 0 0 2rem 0;
  line-height: 1.4;
`

const SuccessModalButton = styled.button`
  background: #e8814a;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background: #d66d3a;
  }
`

class MapSurvey extends React.Component {
  state = {
    scrollPosition: 0,
    hasScrolled: false,
    showSuccessModal: false,
    isSubmitting: false,
    answer: {
      question1: '',
      question2: '',
      question3: '',
    },
  }

  currentPage = React.createRef()

  handleInputChange = (field, value) => {
    this.setState({
      answer: {
        ...this.state.answer,
        [field]: value,
      },
    })
  }

  handleSubmit = async e => {
    e.preventDefault()
    const { accessToken, setSurveyModalStatus } = this.props
    const { answer } = this.state

    this.setState({ isSubmitting: true })

    const surverData = {
      action: 'submitted',
      answer: {
        question1: answer.question1,
        question2: parseInt(answer.question2),
        question3: parseInt(answer.question3),
      },
    }

    try {
      if (accessToken) {
        await ExploreApiService.sendSurveyFeedback(accessToken, surverData)
      } else {
        await ExploreApiService.sendGuestSurveyFeedback(surverData)
      }

      setGACustomEvent('survey_response_submitted', {
        map_helpful: `${answer.question1}`,
        ease_of_use_score: `${answer.question2}`,
        completeness_score: `${answer.question3}`,
        user_type: accessToken ? 'Basic' : 'Guest',
        platform: 'Web',
      })

      this.setState({ showSuccessModal: true })
    } catch (error) {
      console.log(error)
    } finally {
      this.setState({ isSubmitting: false })
      if (accessToken) {
        try {
          const surveyAvailable = await ExploreApiService.getSurveyAvailable(
            accessToken,
          )
          setSurveyModalStatus(surveyAvailable.data.toPrompt)
        } catch (error) {
          console.log(error)
        }
      }
    }
  }

  componentDidMount() {
    this.currentPage.current.addEventListener('scroll', this.handleScroll)
  }

  componentWillUnmount() {
    this.currentPage.current.removeEventListener('scroll', this.handleScroll)
  }

  handleScroll = event => {
    const { hasScrolled: hasScrolledState } = this.state
    const { scrollTop } = event.target
    const hasScrolled = scrollTop > 0

    if (hasScrolled !== hasScrolledState) {
      this.setState({
        hasScrolled,
      })
    }
  }

  handleDismissed = async () => {
    const { accessToken, surveyData } = this.props
    if (accessToken) {
      try {
        await ExploreApiService.sendSurveyFeedback(accessToken, {
          action: 'dismissed',
          answer: surveyData,
        })
      } catch (error) {
        console.log(error)
      } finally {
        await this.getSurveyAvailable()
      }
    } else {
      await ExploreApiService.sendGuestSurveyFeedback({
        action: 'dismissed',
        answer: surveyData,
      })
    }

    setGACustomEvent('survey_skipped', {
      user_type: accessToken ? 'Basic' : 'Guest',
      platform: 'Web',
    })
  }

  handleBackdropClick = async e => {
    if (e.target === e.currentTarget) {
      await this.handleDismissed()
      this.props.displayProfileModal(false)
    }
  }

  handleSuccessModalClose = async () => {
    await this.getSurveyAvailable()
    this.setState({ showSuccessModal: false })
    this.props.displayProfileModal(false)
  }

  getSurveyAvailable = async accessToken => {
    const { setSurveyModalStatus } = this.props
    if (accessToken) {
      try {
        const surveyAvailable = await ExploreApiService.getSurveyAvailable(
          accessToken,
        )
        setSurveyModalStatus(surveyAvailable.data.toPrompt)
      } catch (error) {
        console.log(error)
      }
    }
  }

  render() {
    const { hasScrolled, answer, isSubmitting, showSuccessModal } = this.state
    const { displayProfileModal } = this.props

    return (
      <>
        <MainPage
          ref={this.currentPage}
          onClick={this.handleBackdropClick}
          id="map-survey-modal"
        >
          <ModalContent id="map-survey-modal-content">
            <Header hasScrolled={hasScrolled}>
              <ReturnIcon
                onClick={() => {
                  this.handleDismissed()
                  displayProfileModal(false)
                }}
              >
                <Icon name="x" />
              </ReturnIcon>
              <Title>Map Survey</Title>
            </Header>
            <PageBody>
              <SurveyContainer>
                <SurveyTitle>How's your experience?</SurveyTitle>
                <SurveyDescription>
                  Thank you for using our indoor map. Answer our quick survey
                  and share your experience with us.
                </SurveyDescription>

                <SurveyForm onSubmit={this.handleSubmit}>
                  <QuestionCard>
                    <QuestionLabel>
                      1. Did the map help find what you were looking for?
                    </QuestionLabel>
                    <ButtonGroup>
                      <YesNoButton
                        type="button"
                        selected={answer.question1 === 'yes'}
                        onClick={() =>
                          this.handleInputChange('question1', 'yes')
                        }
                      >
                        YES
                      </YesNoButton>
                      <YesNoButton
                        type="button"
                        selected={answer.question1 === 'no'}
                        onClick={() =>
                          this.handleInputChange('question1', 'no')
                        }
                      >
                        NO
                      </YesNoButton>
                    </ButtonGroup>
                  </QuestionCard>

                  <QuestionCard>
                    <QuestionLabel>
                      2. Rate the ease of use (1-4) / 4 = very easy
                    </QuestionLabel>
                    <ButtonGroup>
                      {[1, 2, 3, 4].map(rating => (
                        <RatingButton
                          key={rating}
                          type="button"
                          selected={answer.question2 === rating.toString()}
                          onClick={() =>
                            this.handleInputChange(
                              'question2',
                              rating.toString(),
                            )
                          }
                        >
                          {rating}
                        </RatingButton>
                      ))}
                    </ButtonGroup>
                  </QuestionCard>

                  <QuestionCard>
                    <QuestionLabel>
                      3. How useful was the information provided? (1-4) / 4 =
                      very useful
                    </QuestionLabel>
                    <ButtonGroup>
                      {[1, 2, 3, 4].map(rating => (
                        <RatingButton
                          key={rating}
                          type="button"
                          selected={answer.question3 === rating.toString()}
                          onClick={() =>
                            this.handleInputChange(
                              'question3',
                              rating.toString(),
                            )
                          }
                        >
                          {rating}
                        </RatingButton>
                      ))}
                    </ButtonGroup>
                  </QuestionCard>

                  <SubmitButton
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !answer.question1 ||
                      !answer.question2 ||
                      !answer.question3
                    }
                  >
                    {isSubmitting ? <Spinner /> : 'Send'}
                  </SubmitButton>
                </SurveyForm>
              </SurveyContainer>
            </PageBody>
          </ModalContent>
        </MainPage>

        {showSuccessModal && (
          <SuccessModalOverlay onClick={this.handleSuccessModalClose}>
            <SuccessModalContent onClick={e => e.stopPropagation()}>
              <SuccessModalTitle>Got it!</SuccessModalTitle>
              <SuccessModalMessage>
                Thank you for your feedback
              </SuccessModalMessage>
              <SuccessModalButton onClick={this.handleSuccessModalClose}>
                Dismiss
              </SuccessModalButton>
            </SuccessModalContent>
          </SuccessModalOverlay>
        )}
      </>
    )
  }
}

MapSurvey.defaultProps = {}

MapSurvey.propTypes = {
  setProfileModalPage: PropTypes.func.isRequired,
  displayProfileModal: PropTypes.func.isRequired,
  accessToken: PropTypes.string.isRequired,
  handleAction: PropTypes.func.isRequired,
  action: PropTypes.string,
}

export default MapSurvey
